// Mock API Service để test khi backend chưa sẵn sàng
export const mockApiService = {
  // Mock authentication endpoints
  login: async (username, password) => {
    console.log('Mock API login called with:', { username, password });
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Mock successful login - accept any valid username and password
    if (username && password && username.length >= 3 && password.length >= 6) {
      // Try to get user data from localStorage first
      const savedUserData = localStorage.getItem('user');
      let userEmail = username.includes('@') ? username : `${username}@example.com`;
      
      if (savedUserData) {
        try {
          const user = JSON.parse(savedUserData);
          userEmail = user.email || userEmail;
        } catch (e) {
          console.log('Could not parse saved user data');
        }
      }
      
      const result = {
        success: true,
        token: 'mock-jwt-token-' + Date.now(),
        user: {
          id: 1,
          email: userEmail,
          name: username,
          username: username
        }
      };
      console.log('Mock API login success:', result);
      return result;
    } else {
      console.error('Mock API login validation failed:', { username, password });
      throw new Error('Invalid credentials');
    }
  },

  register: async (userData) => {
    console.log('Mock API register called with:', userData);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Mock successful registration - accept any valid data
    if (userData.email && userData.username && userData.password) {
      const result = {
        success: true,
        message: 'User registered successfully',
        user: {
          id: Date.now(),
          email: userData.email, // Use the actual email from registration
          name: userData.username,
          username: userData.username
        }
      };
      
      // Save user data to localStorage for future logins
      localStorage.setItem('user', JSON.stringify(result.user));
      console.log('Mock API register success:', result);
      return result;
    } else {
      console.error('Mock API register validation failed:', userData);
      throw new Error('Registration data is invalid');
    }
  },

  // Mock user endpoints
  getProfile: async (userId) => {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      id: userId,
      email: 'test@example.com',
      name: 'Test User',
      username: 'testuser'
    };
  },

  // Mock appointment endpoints
  getAppointments: async (userId, page = 1, limit = 10) => {
    await new Promise(resolve => setTimeout(resolve, 800));
    return {
      appointments: [
        {
          id: 1,
          title: 'Mock Appointment',
          date: new Date().toISOString(),
          status: 'scheduled'
        }
      ],
      total: 1,
      page: page,
      limit: limit
    };
  },

  // Mock chat endpoints
  getConversations: async (userId) => {
    await new Promise(resolve => setTimeout(resolve, 600));
    return {
      conversations: [
        {
          id: 1,
          name: 'Mock Conversation',
          lastMessage: 'Hello!',
          timestamp: new Date().toISOString()
        }
      ]
    };
  },

  // Mock community endpoints
  getPosts: async (page = 1, limit = 10) => {
    await new Promise(resolve => setTimeout(resolve, 700));
    return {
      posts: [
        {
          id: 1,
          title: 'Mock Post',
          content: 'This is a mock post',
          author: 'Test User',
          createdAt: new Date().toISOString()
        }
      ],
      total: 1,
      page: page,
      limit: limit
    };
  }
  ,

  // Mock Messages endpoints
  _ensureMessagesState() {
    const raw = localStorage.getItem('mock_messages');
    if (raw) {
      try { return JSON.parse(raw); } catch (e) { /* fallthrough */ }
    }
    const initial = {
      messages: [
        { id: 1, conversationId: 1, communityRoomId: null, senderId: 1, type: 'text', text: 'Hello from mock', isReadBy: [], createdAt: new Date().toISOString() }
      ]
    };
    localStorage.setItem('mock_messages', JSON.stringify(initial));
    return initial;
  },

  // Dev helpers for messages mock state
  getMockMessagesState() {
    return mockApiService._ensureMessagesState();
  },

  resetMockMessages() {
    const initial = { messages: [] };
    localStorage.setItem('mock_messages', JSON.stringify(initial));
    return initial;
  },

  seedMockMessages(count = 10) {
    const seed = { messages: [] };
    for (let i = 0; i < count; i++) {
      seed.messages.push({ id: Date.now() + i, conversationId: (i % 3) + 1, communityRoomId: (i % 2) ? null : ((i % 5) + 1), senderId: (i % 4) + 1, type: 'text', text: `Seed message ${i+1}`, isReadBy: [], createdAt: new Date(Date.now() - (count-i)*1000).toISOString() });
    }
    localStorage.setItem('mock_messages', JSON.stringify(seed));
    return seed;
  },

  getMessages: async (page = 1, limit = 50) => {
    await new Promise(r => setTimeout(r, 300));
    const state = mockApiService._ensureMessagesState();
    const start = (page - 1) * limit;
    return { messages: state.messages.slice(start, start + limit), total: state.messages.length, page, limit };
  },

  createMessage: async (msg) => {
    await new Promise(r => setTimeout(r, 200));
    if (!msg || !msg.senderId || !msg.text) throw new Error('senderId and text required');
    const state = mockApiService._ensureMessagesState();
    const newMsg = Object.assign({ id: Date.now(), type: msg.type || 'text', communityRoomId: msg.communityRoomId || null, conversationId: msg.conversationId || null, isReadBy: [], createdAt: new Date().toISOString() }, msg);
    state.messages.push(newMsg);
    localStorage.setItem('mock_messages', JSON.stringify(state));
    return newMsg;
  },

  getMessageById: async (id) => {
    await new Promise(r => setTimeout(r, 150));
    const state = mockApiService._ensureMessagesState();
    const m = state.messages.find(x => String(x.id) === String(id));
    if (!m) throw new Error('Message not found');
    return m;
  },

  updateMessage: async (id, patch) => {
    await new Promise(r => setTimeout(r, 200));
    const state = mockApiService._ensureMessagesState();
    const idx = state.messages.findIndex(x => String(x.id) === String(id));
    if (idx === -1) throw new Error('Message not found');
    state.messages[idx] = Object.assign({}, state.messages[idx], patch);
    localStorage.setItem('mock_messages', JSON.stringify(state));
    return state.messages[idx];
  },

  deleteMessage: async (id) => {
    await new Promise(r => setTimeout(r, 150));
    const state = mockApiService._ensureMessagesState();
    const idx = state.messages.findIndex(x => String(x.id) === String(id));
    if (idx === -1) throw new Error('Message not found');
    state.messages.splice(idx,1);
    localStorage.setItem('mock_messages', JSON.stringify(state));
    return { success: true };
  },

  getMessagesByConversation: async (conversationId, page = 1, limit = 100) => {
    await new Promise(r => setTimeout(r, 200));
    const state = mockApiService._ensureMessagesState();
    const filtered = state.messages.filter(m => String(m.conversationId) === String(conversationId));
    const start = (page - 1) * limit;
    return { messages: filtered.slice(start, start + limit), total: filtered.length, page, limit };
  },

  getMessagesByCommunityRoom: async (communityRoomId, page = 1, limit = 100) => {
    await new Promise(r => setTimeout(r, 200));
    const state = mockApiService._ensureMessagesState();
    const filtered = state.messages.filter(m => String(m.communityRoomId) === String(communityRoomId));
    const start = (page - 1) * limit;
    return { messages: filtered.slice(start, start + limit), total: filtered.length, page, limit };
  },

  getMessagesBySender: async (senderId, page = 1, limit = 50) => {
    await new Promise(r => setTimeout(r, 200));
    const state = mockApiService._ensureMessagesState();
    const filtered = state.messages.filter(m => String(m.senderId) === String(senderId));
    const start = (page - 1) * limit;
    return { messages: filtered.slice(start, start + limit), total: filtered.length, page, limit };
  },

  getMessagesByType: async (messageType, page = 1, limit = 50) => {
    await new Promise(r => setTimeout(r, 200));
    const state = mockApiService._ensureMessagesState();
    const filtered = state.messages.filter(m => (m.type || '').toLowerCase() === (messageType || '').toLowerCase());
    const start = (page - 1) * limit;
    return { messages: filtered.slice(start, start + limit), total: filtered.length, page, limit };
  },

  getUnreadMessagesForUser: async (userId) => {
    await new Promise(r => setTimeout(r, 200));
    const state = mockApiService._ensureMessagesState();
    const filtered = state.messages.filter(m => !(m.isReadBy || []).includes(userId));
    return { messages: filtered, total: filtered.length };
  },

  getLatestMessageForConversation: async (conversationId) => {
    await new Promise(r => setTimeout(r, 150));
    const state = mockApiService._ensureMessagesState();
    const filtered = state.messages.filter(m => String(m.conversationId) === String(conversationId));
    const latest = filtered.sort((a,b)=> new Date(b.createdAt) - new Date(a.createdAt))[0] || null;
    return { message: latest };
  },

  getUnreadCountForConversationByUser: async (conversationId, userId) => {
    await new Promise(r => setTimeout(r, 150));
    const state = mockApiService._ensureMessagesState();
    const filtered = state.messages.filter(m => String(m.conversationId) === String(conversationId) && !(m.isReadBy || []).includes(userId));
    return { unreadCount: filtered.length };
  },

  searchMessages: async (query, page = 1, limit = 50) => {
    await new Promise(r => setTimeout(r, 250));
    const state = mockApiService._ensureMessagesState();
    const q = (query || '').toLowerCase();
    const filtered = state.messages.filter(m => (m.text || '').toLowerCase().includes(q));
    const start = (page - 1) * limit;
    return { messages: filtered.slice(start, start + limit), total: filtered.length, page, limit };
  },

  markMessageReadByUser: async (id, userId) => {
    await new Promise(r => setTimeout(r, 150));
    const state = mockApiService._ensureMessagesState();
    const msg = state.messages.find(x => String(x.id) === String(id));
    if (!msg) throw new Error('Message not found');
    msg.isReadBy = Array.from(new Set([...(msg.isReadBy || []), userId]));
    localStorage.setItem('mock_messages', JSON.stringify(state));
    return msg;
  },

  markConversationReadByUser: async (conversationId, userId) => {
    await new Promise(r => setTimeout(r, 200));
    const state = mockApiService._ensureMessagesState();
    const msgs = state.messages.filter(m => String(m.conversationId) === String(conversationId));
    msgs.forEach(m => { m.isReadBy = Array.from(new Set([...(m.isReadBy || []), userId])); });
    localStorage.setItem('mock_messages', JSON.stringify(state));
    return { success: true, updated: msgs.length };
  }
};

// Expose to browser console for convenience
if (typeof window !== 'undefined') {
  try { window.mockApiService = mockApiService; } catch (e) { /* ignore */ }
}

export default mockApiService;
